import { CalendarClock, Key, Ticket } from "lucide-react";
import type { JSX } from "react";

import type { components } from "../../generated/api.js";

/**
 * Die drei Buchungsarten (ADR 0006, StyleGuide 2.4). Aus dem OpenAPI-Vertrag, damit ein
 * neuer oder entfallener Typ beim Übersetzen auffällt und nicht als leeres Badge.
 */
export type ServiceType = components["schemas"]["ServiceType"];

interface TypeMeta {
  label: string;
  /** Die Klasse setzt --c und --c-soft; die Komponenten lesen nur diese. */
  className: string;
  Icon: typeof Ticket;
}

export const SERVICE_TYPES: Record<ServiceType, TypeMeta> = {
  EVENT: { label: "Event", className: "t-event", Icon: Ticket },
  APPOINTMENT: { label: "Termin", className: "t-appointment", Icon: CalendarClock },
  RENT: { label: "Miete", className: "t-rent", Icon: Key },
};

/**
 * Badge der Buchungsart. Erscheint an jedem Angebot — im Feed, in der Suche, im Ticket,
 * in der Backoffice-Tabelle, im Kalender.
 *
 * Farbe trägt nie allein: Symbol und Text stehen immer daneben (StyleGuide 2.4). In der
 * quadratischen Variante bleibt der Text als `aria-label` erhalten.
 */
export function ServiceTypeBadge({
  type,
  square = false,
}: {
  type: ServiceType;
  square?: boolean;
}): JSX.Element {
  const meta = SERVICE_TYPES[type];
  const { Icon } = meta;

  if (square) {
    return (
      <span className={`stype sq ${meta.className}`} aria-label={meta.label} title={meta.label}>
        <Icon size={17} aria-hidden="true" />
      </span>
    );
  }

  return (
    <span className={`stype ${meta.className}`}>
      <Icon size={13} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

/** Nur die Typklasse — für Karten, Ticket-Header und Kalenderblöcke. */
export function serviceTypeClass(type: ServiceType): string {
  return SERVICE_TYPES[type].className;
}
